<template>
  <el-drawer
    v-model="showProxy"
    class="modal-container"
    direction="rtl"
    size="70%"
    :with-header="true"
    :append-to-body="true"
    :modal-append-to-body="true"
    @close="handleClose()"
  >
    <!-- >>>>>> 弹窗头部 <<<<<< -->
    <template #header>
      <div class="header-container">
        <div class="icon-container"></div>
        <span class="title-text">{{ $str.getKey('收益管理/绑定关系') }}</span>
      </div>
    </template>

    <!-- >>>>>> 弹窗内容 <<<<<< -->
    <template #default>
      <div class="content-container">
        <div class="conditions-panel" v-loading="loading">
          <el-form inline :model="formData">
            <el-form-item :label="$str.getKey('收益管理/年份')">
              <el-date-picker v-model="formData.year" type="year" :placeholder="$str.getKey('收益管理/请选择年份')" :clearable="false" :editable="false"></el-date-picker>
            </el-form-item>
            <el-form-item :label="$str.getKey('收益管理/省市')">
              <el-cascader v-model="formData.adCode"  :options="adOptions" @change="handleAdChange" :props="cascaderProps" :show-all-levels="false" clearable />
            </el-form-item>
            <el-form-item :label="$str.getKey('收益管理/电站')">
              <el-select class="station-select" v-model="formData.stationId" :placeholder="$str.getKey('收益管理/请选择电站')" filterable clearable @change="handleStationChange">
                <el-option v-for="item in stationList" :key="item.id" :label="item.name" :value="item.id"></el-option>
              </el-select>
            </el-form-item>
            <el-form-item :label="$str.getKey('收益管理/电表')">
              <el-select class="meter-select" v-model="formData.meterId" :placeholder="$str.getKey('收益管理/请选择电表')" clearable filterable>
                <el-option v-for="item in meterList" :key="item.uuid" :label="item.name" :value="item.uuid"></el-option>
              </el-select>
            </el-form-item>
          </el-form>
        </div>
        <div class="rule-calendar-panel">
          <CalendarPanel :year="formData.year" :ruleList="formData.ruleList"></CalendarPanel>
        </div>
      </div>
    </template>

    <!-- >>>>>> 弹窗底部 <<<<<< -->
    <template #footer>
      <div class="footer-container">
        <el-button class="meter-feature-btn" @click="handleClose()" :disabled="isConfirming">{{ $str.getKey('收益管理/取消') }}</el-button>
        <el-button type="primary" @click="onConfirm" class="meter-feature-btn" :disabled="isConfirming">{{ $str.getKey('收益管理/下发') }}</el-button>
      </div>
    </template>
  </el-drawer>
</template>
<script setup>
import { defineEmits, defineOptions, defineProps, onMounted, reactive, ref, nextTick, watch, computed, provide, watchEffect, inject } from "vue";
import { useVModel } from "@vueuse/core";
import { ElMessageBox } from 'element-plus'
import dayjs from 'dayjs';
import { queryAdRegionInfo, queryMeterSegmentsInfo, queryStationList, queryMeterList, commitBoundData } from "../api/index.js";
import { cloneDeep } from "@/utils"
import CalendarPanel from "./calendarPanel.vue"
import { v4 as uuidv4 } from 'uuid';
import { defaultColorList } from "@/utils/colorHelper/index.js";

const $str = window.$str;
const trackEvent = inject('trackEvent'); 
const trackEventTypes = inject('trackEventTypes');
const trackDatas = reactive({
  commitBind: {
    targetId: '',
    refData: {
      targetName: '电表设置/绑定关系/下发按钮',
      desc: '执行信息提交'
    },
  },
})

 
provide('getRuleColorByDate', getRuleColorByDate);

/** 设置组件配置项 */
defineOptions({
  name: "BindRelationModal"
});

/** 接收父组件信息 */
const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  type: {
    type: String,
    require: true,
  },
});

const emit = defineEmits(['update:show', 'onClose']);
const showProxy = useVModel(props, "show", emit);
const loading = ref(false); // 加载中
const isConfirming = ref(false); // 防抖【连续多次点击提交按钮】

const adOptions = ref([]); // 存储【行政区域】的下拉选项
const stationList = ref([]); // 存储【电站列表】的下拉选项
const meterList = ref([]); // 存储【目标电站下电表列表】的下拉选项
const cascaderProps = { // 级联配置对象
  checkStrictly: true,
  expandTrigger: 'hover',
  emitPath: false, // 只返回当前节点的值
};

// 表单信息
const formData = reactive({
  year: dayjs().format('YYYY'),
  adCode: '', // 示例：山东省-370000
  stationId: '',
  meterId: '',
  ruleList: [],
  dateTypeList: [],
  colorMap: new Map(),

})


// 初始化面板
initPanel();





/** ====== [业务：功能方法] =================================================================== */
/** 初始化：面板信息 */
function initPanel() {
  // 获取省市选项
  getAdRegionOptions();
  getStationList();

}



/** 关闭面板 */
function handleClose(needRefresh = false) {
  // 通知父组件关闭当前弹窗。 参数: 父组件是否需要刷新数据。 
  emit("onClose", props.type, needRefresh);
}



/** 事件响应：行政区域发生变化 */
function handleAdChange(adCode) {
  formData.adCode = adCode;
}


/** 事件响应：监听&响应【年份】【地区】发生变化 */
watchEffect(() => {
  if (formData.year && formData.adCode) {
    refreshCalendarInfo(formData.year, formData.adCode);
  } else {
    formData.ruleList = [];
    formData.colorMap.clear();
    formData.dateTypeList = [];
  }
})


/** 事件响应：选择的电站发生变化 */
function handleStationChange(value) {
  if (value) {
    formData.meterId = '';
    getMeterList(value);
  }
}


/** 提交表单 */
async function onConfirm() {
  // 格式化提交接口需要的参数
  const params = {
    year: dayjs(formData.year).format('YYYY'),
  }

  // 校验：选择省市
  if (formData.adCode) {
    params.provinceCode = formData.adCode;
  } else {
    window.dispatchNotify({
      type: "warning",
      message: $str.getKey('收益管理/请选择省市区域！')
    });
    return false;
  }


  // 校验：选择电表
  if (formData.meterId) {
    const meter = meterList.value.find(it => it.uuid === formData.meterId);
    params.uuid = meter.uuid;
    params.devId = meter.devId;
    params.productModel = meter.productModel;
  }
  if (!params.uuid) {
    window.dispatchNotify({
      type: "warning",
      message: $str.getKey('收益管理/请选择电表！')
    });
    return false;
  }


  // 提交创建
  loading.value = true;
  isConfirming.value = true;
  commitBoundData(params).then(res => {
    // 埋点信息：
    trackDatas.commitBind.refData['year'] = dayjs(formData.year).format('YYYY');
    trackDatas.commitBind.refData['adCode'] = formData.adCode;
    trackDatas.commitBind.refData['stationId'] = formData.stationId;
    trackDatas.commitBind.refData['meterId'] = formData.meterId;
    
    trackEvent(trackEventTypes.otherOperate, trackDatas.commitBind);

    // 操作成功，通知用户
    window.dispatchNotify({
      type: "success",
      message: $str.getKey('收益管理/下发成功！')
    });
    handleClose(true); // 关闭弹窗-通知父组件刷新数据
  }).catch(err => {
    console.log('绑定关系，下发接口异常', err);
  }).finally(() => {
    loading.value = false;
    isConfirming.value = false;
  })
}



/** ====== [辅助：功能方法] =================================================================== */
/** 获取行政区域的下拉选项 */
function getAdRegionOptions() {
  queryAdRegionInfo().then(res => {
    adOptions.value = formatCascaderOption(res?.data);
  })
}


/** 格式化：行政区域下拉选项的数据结构 */
function formatCascaderOption(list) {
  if (Array.isArray(list)) {
    return list.map(item => {
      const newItem = {
        value: item.code,
        label: item.name,
        children: [],
      }
      if (Array.isArray(item.cityList)) {
        newItem.children = formatCascaderOption(item.cityList);
      }
      return newItem;
    })
  }
  console.error('接口返回的行政区域选项应为数组！')
  return [];
}

/** 获取：电站列表的下拉选项 */
function getStationList() {
  const params = {
    pageNum: 1,
    pageSize: 9999,
  }
  queryStationList(params).then(res => {
    if (Array.isArray(res?.rows)) {
      stationList.value = res.rows.map(it => {
        return {
          name: it.name ?? it.stationName ?? it.station?.name ?? '',
          id: it.stationId ?? it.id ?? it.station?.id
        }
      })
    } else {
      stationList.value = [];
    }
  })
}

/** 获取：电表列表的下拉选项 */
function getMeterList(stationId) {
  const params = {
    stationId,
    meterType: 3, // 1-关口电表， 3-并网点电表
  }
  queryMeterList(params).then(res => {
    if (Array.isArray(res?.rows)) {
      meterList.value = res.rows.map(it => {
        return {
          name: it.meterName,
          uuid: it.uuid,
          devId: it.devId,
          productModel: it.productModel,
          meterType: it.meterType,
        }
      })
    } else {
      meterList.value = [];
    }

  })
}



/** 刷新：日历上需要展示的信息 */
function refreshCalendarInfo(year, adCode) {
  if (!year || !adCode) { return false; }

  loading.value = true;
  isConfirming.value = true;
  queryMeterSegmentsInfo(year, adCode).then(res => {
    if (Array.isArray(res?.data?.ruleList)) {
      // 将 elink端 返回的 type 转换成 规则ID, 用于绑定 [日期] [规则] [规则颜色] 三者之间的关系。
      formData.ruleList = res.data.ruleList.map(it => {
        const item = {
          name: it.name,
          id: it.type,
          segments: it.ruleInfo,
          color: initRuleColor(it.type), // eLink返回的 type 是数字，从1开始递增
        }
        return item;
      });
    } else {
      formData.ruleList = [];
      formData.colorMap.clear();
    }
    if (Array.isArray(res?.data?.calendarData)) {
      formData.dateTypeList = res.data.calendarData.map(it => {
        const item = {
          date: it.date,
          ruleId: it.ruleType,
        }
        return item;
      });
    } else {
      formData.dateTypeList = [];
    }
  }).catch(err => {
    console.error('绑定关系，获取日历对应的电表时段信息接口异常',err);
  }).finally(() => {
    loading.value = false;
    isConfirming.value = false;
  })
}


/** 初始化：规则颜色&颜色字典 */
function initRuleColor(key, colorList = defaultColorList) {
  if (!formData.colorMap.has(key)) {
    // 按顺序获取规则颜色
    const idx = formData.colorMap.size;
    const len = colorList.length;
    const color = colorList[idx%len];

    // 将颜色绑定到本次要设置的规则上
    formData.colorMap.set(key, color);
  }
  return formData.colorMap.get(key);
}


/** 获取：日期使用规则对应的颜色 */
function getRuleColorByDate(date) {
  const dateInfo = formData.dateTypeList.find(it => {
    const targetDay = dayjs(date);
    return dayjs(it.date).isSame(targetDay, 'day');
  })
  if (dateInfo && formData.colorMap.has(dateInfo.ruleId)) {
    return formData.colorMap.get(dateInfo.ruleId);
  }

  return '';
}


</script>

<style lang="scss" scoped>
@import '../button-style.scss';

.modal-container {
  & {
    // top: 30px;
  }

  :deep(.el-drawer__header) {
    margin-bottom: 0;
    padding: 14px 16px 10px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }

  :deep(.el-drawer__body) {
    padding: 0;
    overflow: hidden;
    background: rgba(255, 255, 255, 0.5);
    backdrop-filter: blur(10px);
  }

  :deep(.el-drawer__footer) {
    padding: 12px 16px;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
  }

  .header-container {
    & {
      height: auto;
      padding-left: 0;
      display: flex;
      align-items: center;
    }

    .icon-container {
      display: none;
    }

    .title-text {
      font-family: PingFang-SC, PingFang-SC;
      font-size: 16px;
      font-weight: 600;
      color: rgba(46, 46, 46, 0.88);
      text-align: left;
      letter-spacing: 0;
    }
  }

  .content-container {
    & {
      width: 100%;
      box-sizing: border-box;
      padding: 14px 16px 16px;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      gap: 10px;

    }

    .conditions-panel {
      & {
        width: 100%;
        height: 50px;
        // background-color: lime;
        display: flex;
        align-items: center;

      }

      .el-form-item {
        margin-bottom: 0;
      }

      /* 电站/电表选择框稍加长，保证可选择与可读性 */
      :deep(.station-select),
      :deep(.meter-select) {
        width: 220px;
      }
      
    }

    .rule-calendar-panel {
      width: 100%;
      height: 100%;
      flex: 1;
      // background-color: lightcoral;
      min-height: 0;
      
    }

  }

  .footer-container {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 10px;
  }

}
</style>